<template>
  <SoDialog
    v-model="value"
    card-title="New Account Credit"
    width="600"
    @on-close="resetModal()"
  >
    <section>
      <SoForm
        ref="form"
        v-model="isValid"
        data-testid="account-credit-form"
        @submit="handleSubmit()"
      >
        <p class="mb-2">Gross Amount (£)</p>
        <SoTextField
          :model-value="grossAmount"
          :rules="[rules.required, getCreditLimitValidator()]"
          data-testid="account-credit-gross-amount"
          type="number"
          @update:model-value="grossAmount = Number($event)"
        />
        <p class="mb-2">Net Amount (£)</p>
        <SoTextField
          v-model="netAmount"
          data-testid="account-credit-net-amount"
          type="number"
          readonly
          disabled
        />
        <p class="mb-2">Reference - select open complaint</p>
        <SoSelect
          v-model="selectedReference"
          data-testid="account-credit-reference"
          :items="openComplaintOptions"
          :rules="[rules.required]"
          :placeholder="'Select Reference'"
        />
        <p class="mb-2">Reason</p>
        <SoSelect
          v-model="selectedReason"
          data-testid="account-credit-reason"
          :items="reasonOptions"
          :rules="[rules.required]"
          :placeholder="'Select Reason'"
        />
        <p class="mb-2">Description</p>
        <SoTextField
          v-model="descriptionField"
          data-testid="account-credit-description"
          readonly
          disabled
        />

        <template #actions>
          <SoAlert v-if="isSuccess" class="mt-5" type="success"
            >Credit added successfully.
          </SoAlert>
          <SoAlert v-if="submissionStatusError" class="mt-5" type="error">
            {{ submissionStatusError }}
          </SoAlert>
          <div class="account-credit__actions mt-8">
            <v-spacer />
            <SoButton
              data-testid="account-credit-cancel"
              title="Cancel"
              large
              @click="value = false"
            />
            <SoButton
              data-testid="account-credit-create"
              title="Add Credit"
              :disabled="isValid === false || !isDataValid()"
              type="success"
              :loading="isProcessing"
              large
              submit
            />
          </div>
        </template>
      </SoForm>
    </section>
  </SoDialog>
</template>
<script lang="ts">
  import { defineComponent } from "vue"
  import SoDialog from "@/components/SoDialog.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoSelect from "@/components/formControls/SoSelect.vue"
  import SoTextField from "@/components/formControls/SoTextField.vue"
  import { AuthService } from "@/services/AuthService"
  import { AccountCreditService } from "@/services/AccountCreditService"
  import SoForm from "@/components/formControls/SoForm.vue"
  import rules from "@/validators/rules"
  import {
    creditLimitCCA,
    creditLimitSeniorCCA,
  } from "@/validators/creditLimit"
  import { REDUCED_VAT_RATE } from "@/constants/finance"
  import SoAlert from "@/components/SoAlert.vue"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import { SoSelectItem } from "@/types/components/SoSelect"
  import { ROLES } from "@/services/PermissionsService"
  import { errorReporter } from "@/services/ErrorService"
  import errorHandler from "@/services/ErrorService/errorHandler"
  import { AccountService } from "@/services/AccountService"

  export default defineComponent({
    components: {
      SoDialog,
      SoButton,
      SoSelect,
      SoTextField,
      SoForm,
      SoAlert,
    },
    props: {
      modelValue: {
        type: Boolean,
        default: false,
        required: true,
      },
    },
    emits: ["update:modelValue", "closeModal", "submitted"],
    data() {
      return {
        coreId: AccountService.getCoreIdByAccountNumber(
          String(this.$route.params.accountNumber),
        ),
        selectedReason: "",
        selectedReference: "",
        description: "",
        grossAmount: 0,
        reasonOptions: [
          {
            title: "Goodwill",
            value: "Goodwill.",
            description: "Goodwill credit",
          },
        ],
        createCreditMutation: AccountCreditService.createCredit(),
        rules: rules,
        isValid: false,
        isSuccess: false,
        submissionStatusError: "",
        isProcessing: false,
        current: useCurrentAccountStore(),
      }
    },
    computed: {
      value: {
        get() {
          return this.modelValue
        },
        set(value: boolean) {
          this.$emit("update:modelValue", value)
        },
      },
      netAmount() {
        return +(this.grossAmount / REDUCED_VAT_RATE).toFixed(2)
      },
      descriptionField() {
        return this.reasonOptions.find(
          (reason) => reason.value === this.selectedReason,
        )?.description
      },
      openComplaintOptions() {
        return this.current.openComplaints.data.map((complaint) => {
          return {
            title:
              (complaint.internalComplaintCategory ?? "Complaint") +
              " - #" +
              complaint.id,
            value: complaint.id.toString(),
          }
        }) as SoSelectItem[]
      },
    },
    methods: {
      async handleSubmit() {
        this.isProcessing = true
        const accountId = this.coreId.data
        this.isSuccess = false
        this.submissionStatusError = ""
        const username = AuthService.getCurrentUser().username
        try {
          if (!accountId) throw new Error("Missing coreId")
          await this.createCreditMutation.mutateAsync({
            accountId,
            accountCreditReasonName: this.selectedReason,
            grossAmountInPounds: Number(this.grossAmount),
            reference: this.selectedReference,
            description: this.descriptionField,
            salesTaxName: "Reduced VAT",
            userCreated: username,
          })
          this.isSuccess = true
          this.isProcessing = false
          this.clearForm()
          this.$emit("submitted")
        } catch (error) {
          errorReporter.report(error)
          console.error(error)
          this.submissionStatusError = errorHandler.userMessage(
            error,
            "There was an error when processing the account credit",
          )
          this.isProcessing = false
        }
      },
      resetModal() {
        this.clearForm()
        this.isSuccess = false
        this.submissionStatusError = ""
        this.value = false
        this.$emit("closeModal")
      },
      clearForm() {
        const form = this.$refs.form as InstanceType<typeof SoForm>
        form.reset()
      },
      getCreditLimitValidator() {
        const roles = AuthService.getUserRoles()
        if (roles.includes(ROLES.CCA_TL) || roles.includes(ROLES.CCA_SNR)) {
          return creditLimitSeniorCCA
        } else {
          return creditLimitCCA
        }
      },
      isDataValid() {
        return (
          this.selectedReason && this.selectedReference && this.grossAmount > 0
        )
      },
    },
  })
</script>
<style>
  .account-credit__actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }
</style>
